import { reactive, ref, type Reactive, type Ref, type UnwrapNestedRefs } from 'vue';
import { initGeoItemsMeta } from 'src/models/geoItem';
import { useReactiveMasters } from 'src/composables/useMasters';
import type {
  IExtremeMapRef,
  IMovieMetaInfoArea,
  IMoviePlayerControlArea,
} from 'src/models';
import type { MastersContext } from 'src/models/master';
import type {
  LeftRightState,
  MoviePlayerCompareModeState,
  MovieSearchParams,
  MovieSearchState,
} from 'src/models/moviePlayer';

const getMovieDateChoices = (opt: 'back' | 'forward'): Array<{ key: number; val: string }> => {
  const typeMap = { 'back': '前', 'forward': '後' };
  const signMap = { 'back': -1, 'forward': +1 };
  const type = typeMap[opt];
  const sign = signMap[opt];
  return [
    { key: sign * 1, val: `1日${type}` },
    { key: sign * 7, val: `1週間${type}` },
    { key: sign * 14, val: `2週間${type}` },
    { key: sign * 30, val: `1ヶ月${type}` },
    { key: sign * 90, val: `3ヶ月${type}` },
    { key: sign * 180, val: `半年${type}` },
    { key: sign * 365, val: `1年${type}` },
  ];
};

// 実行中に変化しない固定値なのでreactive stateには含めず、
// モジュール定数として持つ（左右で同一の値）。
export const MOVIE_BACK_DATE_CHOICES = getMovieDateChoices('back');
export const MOVIE_FORWARD_DATE_CHOICES = getMovieDateChoices('forward');

interface MoviePlayerCompareModeStateContext {
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>;
  enqueueUiTask: (task: () => Promise<void> | void) => void;
  refExtremeMap: Ref<IExtremeMapRef | undefined>;
  refTopBarArea: Ref<HTMLDivElement | undefined>;
  refVideoArea: Ref<HTMLDivElement | undefined>;
  refControlAreaBoth: Ref<IMoviePlayerControlArea | undefined>;
  refControlAreaLeft: Ref<IMoviePlayerControlArea | undefined>;
  refControlAreaRight: Ref<IMoviePlayerControlArea | undefined>;
  refMetaInfoAreaLeft: Ref<IMovieMetaInfoArea | undefined>;
  refMetaInfoAreaRight: Ref<IMovieMetaInfoArea | undefined>;
  masters: Reactive<MastersContext>;
  resolveMastersPromise: () => Promise<void>;
}

// MoviePlayerCompareModeのstate・refs・非同期マスタ取得のみを提供する。
// 他のcomposableは構築せず、MoviePlayerCompareMode/index.vue側でDI順に直接構築する。
export function useMoviePlayerCompareModeState(): MoviePlayerCompareModeStateContext {
  const initLeftRightState = (): LeftRightState => {
    return {
      movieList: {
        listSelection: {
          currentMovieLists: [],
          currentMovieListId: '',
          currentMovieList: null,
        },
        current: {
          currentMovie: null,
          currentMovieIdx: -1,
          isFirstMovie: false,
          isLastMovie: false,
          isTryingToPlayAnotherMovie: false,
        },
        timeDisp: {
          startTimeDisp: '00:00:00',
          endTimeDisp: '00:00:00',
        },
      },
      geo: {
        currentMgi: null,
        currentMgiIdx: -1,
        locationDisp: '',
        currentMovieCurrentDt: null,
        mapElemInfo: null,
        createMapElemFailed: 0,
      },
      playback: {
        currentPlaySpeed: 1.0,
        isPlayBackward: false,
        isPlaying: false,
        seek: {
          current: 0,
          min: 0,
          max: 100,
          step: 1,
        },
        isSeeking: false,
      },
      isMovieReady: false,
    };
  };
  // baseDateがnullの間は実際には参照されないプレースホルダ。
  // 実値はsearchMoviesByDateRangeで設定される。
  const initMovieSearchParams = (): MovieSearchParams => ({
    dtFrom: new Date(),
    dtTo: new Date(),
  });
  const initMovieSearchState = (): MovieSearchState => {
    return {
      left: {
        params: initMovieSearchParams(),
        baseDate: null,
        isResultEmpty: false,
      },
      right: {
        params: initMovieSearchParams(),
        baseDate: null,
        isResultEmpty: false,
      },
    };
  };
  const state = reactive<MoviePlayerCompareModeState>({
    ui: {
      isInitialized: false,
      activeVjsLrMap: {},
      selectedCameraPosition: 'front',
      bulkControl: true,
    },
    both: {
      isPlayBackward: false,
      isPlaying: false,
    },
    left: initLeftRightState(),
    right: initLeftRightState(),
    movieSearch: initMovieSearchState(),
    geoItemsMeta: initGeoItemsMeta(),
    uiPromise: Promise.resolve(),
  });

  // state.uiPromiseはUI操作の直列化キュー。ここでawait/try-catchするのは、
  // 中間ステップの例外でチェーン全体が無音のまま壊れる
  // （以降のenqueueUiTaskが二度と実行されなくなる）のを防ぐため。
  const enqueueUiTask = (task: () => Promise<void> | void): void => {
    state.uiPromise = (async() => {
      await state.uiPromise;
      try {
        await task();
      } catch (e) {
        console.error(e);
      }
    })();
  };

  const refExtremeMap = ref<IExtremeMapRef>();
  const refTopBarArea = ref<HTMLDivElement>();
  const refVideoArea = ref<HTMLDivElement>();
  const refControlAreaBoth = ref<IMoviePlayerControlArea>();
  const refControlAreaLeft = ref<IMoviePlayerControlArea>();
  const refControlAreaRight = ref<IMoviePlayerControlArea>();
  const refMetaInfoAreaLeft = ref<IMovieMetaInfoArea>();
  const refMetaInfoAreaRight = ref<IMovieMetaInfoArea>();
  const { masters, resolveMastersPromise } = useReactiveMasters();

  return {
    state,
    enqueueUiTask,
    refExtremeMap,
    refTopBarArea,
    refVideoArea,
    refControlAreaBoth,
    refControlAreaLeft,
    refControlAreaRight,
    refMetaInfoAreaLeft,
    refMetaInfoAreaRight,
    masters,
    resolveMastersPromise,
  };
}
